import { useQuery } from "@tanstack/react-query";
import { Activity, Database, CheckCircle2, XCircle, Users, AlertTriangle, Boxes, PlugZap } from "lucide-react";
import api from "@/lib/api";
import { money } from "@/lib/format";

function Dot({ ok }) {
  return ok ? <CheckCircle2 size={16} className="text-[#5C8A72]" /> : <XCircle size={16} className="text-[#B8624B]" />;
}

export default function SystemHealthPanel() {
  const { data: h } = useQuery({ queryKey: ["system-health"], queryFn: () => api.get("/admin/system-health").then((r) => r.data), refetchInterval: 30000 });
  const { data: ov } = useQuery({ queryKey: ["reports-overview"], queryFn: () => api.get("/admin/reports/overview").then((r) => r.data) });
  if (!h) return <div className="text-[#4A5D6B]">Loading system health…</div>;

  const upMin = Math.floor((h.uptime_seconds || 0) / 60);
  return (
    <div data-testid="system-health-panel">
      <div className="flex items-center gap-2 mb-6">
        <Activity size={22} className="text-[#144F75]" />
        <h2 className="font-serif text-2xl text-[#1A242B]">System Health</h2>
        <span className={`ml-2 text-xs px-3 py-1 rounded-full ${h.status === "healthy" ? "bg-[#5C8A72]/15 text-[#5C8A72]" : "bg-[#B8624B]/15 text-[#B8624B]"}`} data-testid="health-status">{h.status}</span>
      </div>

      {ov && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-8">
          <Kpi label="Members" v={ov.total_members} />
          <Kpi label="Bookings" v={ov.total_bookings} />
          <Kpi label="Orders" v={ov.total_orders} />
          <Kpi label="POS revenue" v={`${money(ov.pos_revenue)}`} />
          <Kpi label="Steward raised" v={`${money(ov.stewardship_raised)}`} />
          <Kpi label="Testimonials" v={ov.approved_testimonials} />
          <Kpi label="Locations" v={ov.active_locations} />
        </div>
      )}

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
        <Card title="Database" icon={Database}>
          <Row label="Connection" ok={h.database.connected} />
          <Row label="Uptime" value={`${upMin} min`} />
        </Card>
        <Card title="Integrations" icon={PlugZap}>
          {Object.entries(h.integrations).map(([k, v]) => <Row key={k} label={k.replace(/_/g, " ")} ok={v} />)}
        </Card>
        <Card title="Content" icon={Boxes}>
          {Object.entries(h.content).map(([k, v]) => <Row key={k} label={k.replace(/_/g, " ")} value={v} />)}
        </Card>
        <Card title="Action queues" icon={AlertTriangle}>
          {Object.entries(h.queues).map(([k, v]) => <Row key={k} label={k.replace(/_/g, " ")} value={v} warn={v > 0} />)}
        </Card>
      </div>

      <div className="eyw-card p-6 mt-5">
        <h3 className="font-medium text-[#1A242B] mb-3 flex items-center gap-2"><Users size={17} /> Members by role</h3>
        <div className="flex flex-wrap gap-2">
          {Object.entries(h.members_by_role).map(([r, c]) => (
            <span key={r} className="text-xs px-3 py-1.5 rounded-full bg-[#F1EDE6] text-[#4A5D6B]" data-testid={`role-count-${r}`}>{r}: <b className="text-[#144F75]">{c}</b></span>
          ))}
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, v }) {
  return <div className="eyw-card p-4"><div className="font-serif text-2xl text-[#144F75]">{v}</div><p className="text-xs text-[#4A5D6B] mt-1">{label}</p></div>;
}
function Card({ title, icon: Icon, children }) {
  return <div className="eyw-card p-5"><h3 className="font-medium text-[#1A242B] mb-3 flex items-center gap-2"><Icon size={16} className="text-[#144F75]" /> {title}</h3><div className="space-y-1.5">{children}</div></div>;
}
function Row({ label, ok, value, warn }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-[#4A5D6B] capitalize">{label}</span>
      {ok !== undefined ? <Dot ok={ok} /> : <span className={`font-medium ${warn ? "text-[#B8624B]" : "text-[#1A242B]"}`}>{value}</span>}
    </div>
  );
}
